import { useEffect, useState } from "react";
import moment from "moment";
import OptOutModal from "./OptOutModal";
import MoreInfoHistoryPopUp from "./MoreInfoHistoryPopUp/MoreInfoHistoryPopUp";
import { FormattedMessage, useIntl } from "react-intl";
import StaticTable from "../StaticTable/StaticTable";
import DateToolTip from "../DateToolTip/DateToolTip";
import useHover from "../../hooks/useHover";
import Tooltip from "../Tooltip/Tooltip";
import PropTypes from "prop-types";
import HeaderWithTooltip from "../StaticTable/HeaderWithTooltip";
const ConsentProcessing = ({ refresh, details, loading, setLoading }) => {
  const intl = useIntl();
  const [consentProcessingData, setConsentProcessingData] = useState([]);
  const [optOutOpen, setOptOutOpen] = useState(false);
  const [optOutRowData, setOptOutRowData] = useState();
  const [selectedConsent, setSelectedConsent] = useState();
  const [isProfileHistoryOpen, setIsProfileHistoryOpen] = useState(false);
  const { handleMouseEnter, handleMouseLeave, hoveredData, ishovered } =
    useHover();
  const [isAnyPurposeActive, setIsAnyPurposeActive] = useState(false);

  const getConsentProcessingData = () => {
    if (Array.isArray(details?.data_processing_authorizations)) {
      const allData = details?.data_processing_authorizations;
      const consentProcessing = Object.groupBy(
        allData,
        (allData) => allData.initial_legal_basis
      );
      setConsentProcessingData(consentProcessing?.consent || []);
      setIsAnyPurposeActive(
        consentProcessing?.consent?.some((item) => item.status == "Active")
      );
    }
  };
  useEffect(() => {
    if (
      details?.digital_identity?.type &&
      details?.digital_identity?.value &&
      details?.digital_identity?.controller_code &&
      details?.digital_identity?.customer_scope
    )
      getConsentProcessingData();
  }, [details]);

  const onCallback = () => {
    setLoading(true);
    setTimeout(refresh, "3000");
  };

  const columns = [
    {
      header: intl.formatMessage({
        id: "id.label.processing-purpose",
        defaultMessage: "Processing Purpose",
      }),
      accessorKey: "global_purpose",
    },
    {
      header: intl.formatMessage({
        id: "label.status",
        defaultMessage: "Status",
      }),
      accessorKey: "status",
      cell: function render(row) {
        return (
          <span
            className={
              row?.row.original?.status === "Active"
                ? `text-active`
                : `text-inactive`
            }
          >
            {row?.row.original?.status}
          </span>
        );
      },
    },
    {
      header: intl.formatMessage({
        id: "label.status-reason",
        defaultMessage: "Status Reason",
      }),
      accessorKey: "status_reason",
    },
    {
      header: (
        <HeaderWithTooltip tooltipText="Value based on the Time Zone setting in the Configurations">
          <Tooltip position="right" text={"Date format: DD.MM.YYYY"}>
            <span>
              {intl.formatMessage({
                id: "label.capture-date",
                defaultMessage: "Capture Date",
              })}
            </span>
          </Tooltip>
        </HeaderWithTooltip>
      ),
      accessorKey: "capture_date",
      cell: (info) => {
        const value = info.getValue();
        if (value === null) {
          return "N/A";
        }
        if (moment(value).isValid()) {
          return moment(value).format("DD.MM.YYYY");
        }
        return "Invalid Date";
      },
    },
    {
      header: (
        <HeaderWithTooltip tooltipText="Value based on the Time Zone setting in the Configurations">
          <Tooltip position="right" text={"Time format: HH:MM"}>
            <span >
              {intl.formatMessage({
                id: "label.capture-time",
                defaultMessage: "Capture Time",
              })}
            </span>
          </Tooltip>
        </HeaderWithTooltip>
      ),
      accessorKey: "capture_time",
      cell: (info) => {
        const value = info.getValue();
        if (value === undefined || value === null || value === "") {
          return "N/A";
        }
        if (moment(value, "HH:mm:ss.SSS", true).isValid()) {
          return (
            <span className="pe-1">
              {moment(value, "HH:mm:ss.SSS").format("HH:mm")}
            </span>
          );
        }
        return "Invalid Date";
      },
    },
    {
      header: intl.formatMessage({
        id: "label.capture-source",
        defaultMessage: "Capture Source",
      }),
      accessorKey: "capture_source",
    },
    {
      header: () => {
        return (
          <Tooltip position="right" text={"Date format: DD.MM.YYYY"}>
            <span className="pe-1">
              {intl.formatMessage({
                id: "label.expiry-date",
                defaultMessage: "Expiry Date",
              })}
            </span>
          </Tooltip>
        );
      },
      accessorKey: "expiry_date",
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value == undefined) {
          return "N/A";
        }
        if (moment(value).isValid()) {
          return moment.utc(value).local().format("DD.MM.YYYY");
        }
        return "Invalid Date";
      },
    },
    {
      header: () => {
        return (
          <Tooltip position="right" text={"Date format: DD.MM.YYYY"}>
            <span className="pe-1">
              {intl.formatMessage({
                id: "label.last-status-change-date",
                defaultMessage: "Last Status Change",
              })}
            </span>
          </Tooltip>
        );
      },
      accessorKey: "last_status_change_datetime",
      cell: (info) => {
        const value = info.getValue();
        if (value === null) {
          return "N/A";
        }
        if (moment(value).isValid()) {
          return (
            <div
              onMouseEnter={() => handleMouseEnter(value)}
              onMouseLeave={handleMouseLeave}
            >
              <span className="pe-1">
                {moment.utc(value).local().format("DD.MM.YYYY HH:mm")}
              </span>
              <DateToolTip ishovered={ishovered} hoveredData={hoveredData} />
            </div>
          );
        }
        return "Invalid Date";
      },
    },
    {
      header: intl.formatMessage({
        id: "label.collector-email",
        defaultMessage: "Collector Email",
      }),
      accessorKey: "collector_email",
      cell: (info) => {
        const value = info.getValue();
        if (value === null) {
          return "N/A";
        } else {
          return value;
        }
      },
    },
    {
      header: intl.formatMessage({
        id: "label.action",
        defaultMessage: "Action",
      }),
      accessorKey: "actions",
      cell: ({ row }) => {
        const status = row.getValue("status");
        return status === "Active" ? (
          <button
            className="btn btn-danger border-danger"
            onClick={(e) => {
              e.stopPropagation();
              setOptOutOpen(true);
              setOptOutRowData(row?.original);
            }}
          >
            <FormattedMessage id="id.label.optout" defaultMessage="OPT OUT" />
          </button>
        ) : null;
      },
    },
  ];
  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 col-lg-12 px-0">
            <div className="custom-border">
              <div className="d-flex justify-content-end align-items-center search-bar-container px-3 py-2">
                {isAnyPurposeActive && (
                  <button
                    id="create-new-person"
                    className="btn btn-danger bg-danger-subtle text-danger border-danger-subtle text-uppercase"
                    onClick={() => {
                      setOptOutOpen(true);
                      setOptOutRowData({
                        deactivate_all_consent: true,
                      });
                    }}
                  >
                    <FormattedMessage
                      id="id.label.optout-all-consent"
                      defaultMessage="Withdraw all consent"
                    />
                  </button>
                )}
              </div>
              <StaticTable
                columns={columns}
                data={consentProcessingData}
                customClass={"consent-processing"}
                defaultErrorMessage={"No DPA records found"}
                messageId={"id.cp-li.table.no-dpa-records"}
                loading={loading}
                setIsProfileHistoryOpen={setIsProfileHistoryOpen}
                setSelectedConsent={setSelectedConsent}
              />
            </div>
          </div>
        </div>
      </div>
      <MoreInfoHistoryPopUp
        isProfileHistoryOpen={isProfileHistoryOpen}
        setIsProfileHistoryOpen={setIsProfileHistoryOpen}
        setSelectedData={setSelectedConsent}
        digitalIdentity={details?.digital_identity}
        history={details?.history?.[selectedConsent?.global_purpose]}
        historyInfo={selectedConsent}
      />
      <OptOutModal
        open={optOutOpen}
        setOpen={setOptOutOpen}
        data={optOutRowData}
        getList={onCallback}
        userDetails={{ ...details?.digital_identity }}
      />
    </>
  );
};

ConsentProcessing.propTypes = {
  refresh: PropTypes.func,
  details: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
};

export default ConsentProcessing;
